import { OmegakeittiotMachineToolType } from "src/api/generated/tenants/omegakeittiot/machineTools/model/omegakeittiotMachineToolType.ts";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { OmegakeittiotMachineToolsApi } from "src/api/generated/tenants/omegakeittiot/machineTools/omegakeittiotMachineToolsApi.ts";
import { OmegakeittiotMachineToolInstructionLine } from "src/api/generated/tenants/omegakeittiot/machineTools/model/omegakeittiotMachineToolInstructionLine.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faCloudDownloadAlt } from "@fortawesome/pro-regular-svg-icons";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface OmegakeittiotMachineToolInstructionGenerationDataGridProps {
	shopOrderBatchIds: number[];
	machineType: OmegakeittiotMachineToolType;
	isActive?: boolean;
}

interface GridDataWithIndex extends OmegakeittiotMachineToolInstructionLine {
	lineId: number;
}

interface FetchParams {
	searchQuery: string;
}

export const OmegakeittiotMachineToolInstructionGenerationDataGrid = ({
	shopOrderBatchIds,
	machineType,
}: OmegakeittiotMachineToolInstructionGenerationDataGridProps) => {
	return (
		<ClientSideDataGridModel<GridDataWithIndex[], GridDataWithIndex, FetchParams>
			gridId={"BC2D1AEF0FA38655"}
			fetchData={async ({ searchQuery }) => {
				const data = await OmegakeittiotMachineToolsApi.getMachineToolInstructionViewData({
					shopOrderBatchIds: shopOrderBatchIds,
					machineType: machineType,
					searchQuery: searchQuery,
				});
				return data.map((line, index) => ({
					lineId: index,
					...line,
				}));
			}}
			getRows={(data) => data}
			getRowId={(row) => row.lineId}
			initialParams={{
				searchQuery: "",
			}}
			render={({ dataGridProps, refreshData }) => {
				return (
					<FormCrudDataGrid<GridDataWithIndex>
						columns={[
							integerColumn({
								field: "shopOrderBatchId",
								headerName: "LotNumber",
							}),
							textColumn({
								field: "article",
								headerName: "Article",
							}),
							textColumn({
								field: "description",
								headerName: "Description",
							}),
							textColumn({
								field: "note",
								headerName: "Note",
							}),
							floatColumn({
								field: "qty",
								headerName: "Qty",
							}),
							textColumn({
								field: "program",
								headerName: "Program",
							}),
							textColumn({
								field: "material",
								headerName: "Material",
							}),
							floatColumn({
								field: "thickness",
								headerName: "Thickness",
							}),
							floatColumn({
								field: "height",
								headerName: "Height",
							}),
							floatColumn({
								field: "width",
								headerName: "Width",
							}),
							floatColumn({
								field: "grainDirection",
								headerName: "GrainDirection",
							}),
							floatColumn({
								field: "margin",
								headerName: "Margin",
							}),
							floatColumn({
								field: "priority",
								headerName: "Priority",
							}),
							textColumn({
								field: "upperCode0",
								headerName: "UpperCode0",
							}),
							textColumn({
								field: "upperCode1",
								headerName: "UpperCode1",
							}),
							textColumn({
								field: "up",
								headerName: "Up",
							}),
							textColumn({
								field: "down",
								headerName: "Down",
							}),
							textColumn({
								field: "left",
								headerName: "Left",
							}),
							textColumn({
								field: "right",
								headerName: "Right",
							}),
							textColumn({
								field: "programPath",
								headerName: "ProgramPath",
							}),
							machineType === "ROVER" &&
								textColumn({
									field: `parameter1` as any,
									headerName: `Parametri 1`,
								}),
						]}
						actionBarComponents={
							<>
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={async (input) => {
										await refreshData({ searchQuery: input });
									}}
								/>
								<AsyncButton
									icon={faCloudDownloadAlt}
									label={"Lataa työstökoneohjelma"}
									variant={"outlined"}
									onClick={async () => {
										const fileHandle = await OmegakeittiotMachineToolsApi.generateInstructionFile({
											shopOrderBatchIds: shopOrderBatchIds,
											machineType: machineType,
										});
										downloadFile(fileHandle);
									}}
								/>
								<AsyncButton
									icon={faCloudDownloadAlt}
									label={"Lataa PDF"}
									variant={"outlined"}
									onClick={async () => {
										const fileHandle =
											await OmegakeittiotMachineToolsApi.generateInstructionPrintout({
												shopOrderBatchIds: shopOrderBatchIds,
												machineType: machineType,
											});
										downloadFile(fileHandle);
									}}
								/>
							</>
						}
						agentContext={{
							key: defineAgentUiContextKey("tenant.omegakeittiot.machineToolInstructions.grid"),
							data: { shopOrderBatchIds, machineType },
							rowFields: ["lineId", "shopOrderBatchId", "article", "program"],
							selectedRowPathSegment: (row) => `${row.shopOrderBatchId} ${row.article}`,
							selectedRowsPathSegment: (count) => `${count} riviä valittu`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
